import type { ResourceType } from '__types__';
import store from 'store';
import { apiClient } from 'v4/core/apiClient';
import { syncResourceTypeUpdatedToRedux } from 'v5/features/resource-types-admin/sync/syncResourceTypeToRedux';
import { createResourceTypeBindingsHttpApi } from './resource-type-bindings.api.http';
import {
  flattenResourceTypeBindings,
  parseBindingSourceRowsFromRedux,
  type ResourceTypeWorkspaceRelation
} from './resource-type-bindings.schema';

/**
 * Production wiring for {@link createResourceTypeBindingsHttpApi}.
 *
 * ## Why this couples to v1 Redux
 *
 * There is no dedicated bindings GET. Auth/settings already returns each
 * resource type with nested `enterprise_workspaces` (join extras: `enabled`,
 * `workspace_metastructure`). That payload lands in Redux on
 * `LOAD_AUTH_SUCCESS` as an untyped runtime bag on each type row.
 *
 * Bindings are the **only** module that should read those nested rows: `list()`
 * zod-parses them and throws on malformed rows (we own this bag). The public
 * {@link ResourceType} model does not expose `enterprise_workspaces` — call
 * {@link ResourceTypeBindingsService} instead.
 *
 * After `PUT .../workspace-binding`, the HTTP adapter GETs the type (graph
 * includes workspaces) and calls `onBindingsSourceUpdated` so Redux stays in
 * sync; otherwise RQ invalidate → `list()` would still see the pre-write blob
 * until a full auth/settings reload.
 *
 * ## What is injected (minimal)
 *
 * - `readBindings` — flatten zod-parsed join extras from `resource_types.data`
 * - `onBindingsSourceUpdated` — sync type + relations after GET (preserves
 *   join extras without putting them on the typed ResourceType surface)
 *
 * Initial settings fetch is owned by auth, not this module.
 * The HTTP factory itself must not import `store` or sync helpers.
 */
function readBindingsFromRedux() {
  return flattenResourceTypeBindings(
    parseBindingSourceRowsFromRedux(store.getState())
  );
}

function syncBindingsSourceToRedux(
  type: ResourceType,
  workspaceRelations: ResourceTypeWorkspaceRelation[]
) {
  syncResourceTypeUpdatedToRedux(type, { workspaceRelations });
}

export const myResourceTypeBindingsApi = createResourceTypeBindingsHttpApi(
  apiClient,
  {
    readBindings: readBindingsFromRedux,
    onBindingsSourceUpdated: syncBindingsSourceToRedux
  }
);
